import { useState, useEffect, JSX } from "react";
import { WifiOff, CheckCircle2, RefreshCw } from "lucide-react";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import { checkConnection, useNetworkStatus } from "../hooks/useNetworkStatus";
import { t } from "i18next";

/* ─── Only what Tailwind can't express: staggered nth-child dot animation ── */
const animationStyles = `
  @keyframes nco-dot-blink {
    0%, 80%, 100% { opacity: 0.15; transform: scale(0.85); }
    40%           { opacity: 1;    transform: scale(1); }
  }
  .nco-dot { animation: nco-dot-blink 1.4s ease-in-out infinite; }
  .nco-dot:nth-child(2) { animation-delay: 0.2s; }
  .nco-dot:nth-child(3) { animation-delay: 0.4s; }
`;

interface NoConnectionOverlayProps {
  /**
   * Controlled open state. When used with `useConnectionGuard`, pass
   * the `open` value from the hook so the parent can trigger the overlay.
   * Falls back to self-managed state when omitted (standalone usage).
   */
  open?: boolean;
  /** Controlled setter — pair with `open`. */
  onOpenChange?: (open: boolean) => void;
}

export function NoConnectionOverlay({
  open: openProp,
  onOpenChange,
}: NoConnectionOverlayProps = {}): JSX.Element {
  const { isOnline, wasOffline } = useNetworkStatus();

  // Internal open state used only in uncontrolled (standalone) mode
  const [openInternal, setOpenInternal] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [retrying, setRetrying] = useState<boolean>(false);

  // Resolve whether we're in controlled or uncontrolled mode
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : openInternal;
  const setOpen = (value: boolean): void => {
    if (isControlled) {
      onOpenChange?.(value);
    } else {
      setOpenInternal(value);
    }
  };

  // Uncontrolled only: open when browser detects offline
  useEffect(() => {
    if (!isControlled && !isOnline) {
      setShowSuccess(false);
      setOpenInternal(true);
    }
  }, [isOnline, isControlled]);

  // Show success state briefly on reconnect, then close
  useEffect(() => {
    if (isOnline && wasOffline && open) {
      setShowSuccess(true);
      setRetrying(false);
      const timer = setTimeout(() => {
        setOpen(false);
        setShowSuccess(false);
      }, 1800);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline, open]);

  // Reset success state whenever the overlay re-opens while offline
  useEffect(() => {
    if (open && !isOnline) setShowSuccess(false);
  }, [open, isOnline]);

  const handleRetry = async (): Promise<void> => {
    setRetrying(true);
    const online = await checkConnection();
    setRetrying(false);
    if (!online) {
      // Ensure overlay stays/reappears — relevant in uncontrolled mode
      setOpen(true);
    }
    // If online, the "online" event triggers wasOffline effect → success state
  };

  return (
    <>
      <style>{animationStyles}</style>

      {/*
        DialogContent by default renders as a centered modal.
        We override positioning to pin it to the bottom of the screen,
        matching the original slide-up-from-bottom design.
      */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="
            fixed bottom-4 left-1/2 top-auto z-[9999] w-[calc(100%-2rem)]
            max-w-[420px] -translate-x-1/2 translate-y-0
            border-none bg-transparent p-0 shadow-none
            duration-300  data-[state=open]:animate-in data-[state=closed]:animate-out
            data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom-8
            data-[state=open]:slide-in-from-bottom-8
            sm:top-auto
          "
          overlayClassName="z-[180]"
          aria-label="No internet connection"
        >
          <Card className="w-full rounded-[20px] shadow-2xl">
            <CardContent className="flex flex-col items-center px-9 pb-7 pt-8 text-center">
              {/* Icon */}
              <div className="relative mb-6 flex h-16 w-16 items-center justify-center">
                {!showSuccess && (
                  <span className="absolute inset-[-8px] animate-ping rounded-full border-[2.5px] border-red-500 opacity-60" />
                )}
                <div
                  className={`
                    flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full border-2
                    ${
                      showSuccess
                        ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950"
                        : "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950"
                    }
                  `}
                >
                  {showSuccess ? (
                    <CheckCircle2
                      size={30}
                      className="text-green-500"
                      aria-hidden="true"
                    />
                  ) : (
                    <WifiOff
                      size={30}
                      className="text-red-500"
                      aria-hidden="true"
                    />
                  )}
                </div>
              </div>

              {/* Text */}
              {showSuccess ? (
                <>
                  <p className="mb-2 text-lg font-bold tracking-tight">
                    {t("back-online")}!
                  </p>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {t("connection-restored")}
                  </p>
                </>
              ) : (
                <>
                  <p className="mb-2 text-lg font-bold tracking-tight">
                    {t("no-internet")}
                  </p>
                  <p className="mb-7 text-sm leading-relaxed text-muted-foreground">
                    {t("require-online-connection")}
                  </p>

                  {/* Animated dots */}
                  <div
                    className="mb-7 flex justify-center gap-[7px]"
                    aria-hidden="true"
                  >
                    <span className="nco-dot h-[7px] w-[7px] rounded-full bg-red-500" />
                    <span className="nco-dot h-[7px] w-[7px] rounded-full bg-red-500" />
                    <span className="nco-dot h-[7px] w-[7px] rounded-full bg-red-500" />
                  </div>

                  {/* Retry button */}
                  <Button
                    onClick={handleRetry}
                    disabled={retrying}
                    aria-label="Retry connection"
                    className="flex w-full gap-2"
                  >
                    <RefreshCw
                      size={16}
                      aria-hidden="true"
                      className={retrying ? "animate-spin" : ""}
                    />
                    {retrying ? `${t("checking")}...` : t("try-again")}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
    </>
  );
}
